/**
 * KeyLevels Component - Sticky Support & Resistance Display
 * 
 * Session 40: Hysteresis-based S&R with status indicators
 * 
 * Features:
 * - Fetches sticky levels from /api/gex/sticky-levels
 * - Displays wall status with color coding:
 *   - STABLE (Green): OI > 95% of peak
 *   - WEAKENING (Orange): OI 80-95% of peak
 *   - COLLAPSING (Red): OI < 80% of peak
 *   - BROKEN (Flashing Red): Price > Strike
 * - Shows OI strength percentage
 * - Real-time price break countdown
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, Zap, Lock, Unlock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status configuration with colors and icons
const STATUS_CONFIG = {
  STABLE: {
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/40',
    icon: Shield,
    label: 'STABLE',
    pulse: false,
  },
  WEAKENING: {
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/40',
    icon: AlertTriangle,
    label: 'WEAKENING',
    pulse: false,
  },
  COLLAPSING: {
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/40',
    icon: Zap,
    label: 'COLLAPSING',
    pulse: true,
  },
  BROKEN: {
    color: 'text-red-500',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/60',
    icon: Unlock,
    label: 'BROKEN',
    pulse: true,
  },
  ZOMBIE: {
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/20',
    borderColor: 'border-gray-500/40',
    icon: AlertTriangle,
    label: 'ZOMBIE',
    pulse: true,
  },
};

// Wall Status Badge Component
const WallStatusBadge = memo(({ status, oiStrength, isBreaking, breakDuration }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.STABLE;
  const Icon = config.icon;
  
  // Format break duration (seconds to mm:ss)
  const formatBreakTime = (seconds) => {
    if (!seconds || seconds <= 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[7px] font-bold ${config.bgColor} ${config.borderColor} border ${config.pulse ? 'animate-pulse' : ''}`}>
      <Icon className={`w-2.5 h-2.5 ${config.color}`} />
      <span className={config.color}>{config.label}</span>
      {oiStrength > 0 && status !== 'BROKEN' && (
        <span className="text-muted-foreground ml-0.5">{oiStrength}%</span>
      )}
      {isBreaking && breakDuration > 0 && (
        <span className="text-red-300 ml-1">⏱{formatBreakTime(breakDuration)}/3:00</span>
      )}
    </div>
  );
});

WallStatusBadge.displayName = 'WallStatusBadge';

// Single Wall Card Component
const WallCard = memo(({ 
  type,  // 'call' or 'put'
  wallData, 
  currentPrice,
  distance 
}) => {
  const isCall = type === 'call';
  const baseColor = isCall ? 'sell' : 'buy';  // Call = Resistance (red), Put = Support (green)
  const label = isCall ? 'Call Wall' : 'Put Wall';
  const subLabel = isCall ? 'RESISTANCE' : 'SUPPORT';
  
  if (!wallData || wallData.strike === 0) {
    return (
      <div className={`bg-${baseColor}/5 border border-${baseColor}/20 rounded p-1.5 text-center opacity-50`} data-testid={`${type}-wall-card`}>
        <div className={`text-[8px] text-${baseColor}/50 uppercase font-semibold`}>{label}</div>
        <div className="text-sm font-bold text-muted-foreground font-mono">--</div>
        <div className="text-[7px] text-muted-foreground" data-testid={`${type}-wall-scanning`}>Scanning...</div>
      </div>
    );
  }
  
  const { strike, status, oi_strength, gex, is_breaking, break_duration, peak_oi, current_oi } = wallData;
  
  // Calculate distance
  const distanceValue = currentPrice 
    ? (isCall ? strike - currentPrice : currentPrice - strike).toFixed(0)
    : '--';
  const distanceDir = isCall 
    ? (distanceValue > 0 ? '↑' : '↓')
    : (distanceValue > 0 ? '↑' : '↓');
  
  return (
    <div className={`bg-${baseColor}/10 border border-${baseColor}/30 rounded p-1.5 text-center relative overflow-hidden`} data-testid={`${type}-wall-card`}>
      {/* Flashing overlay for BROKEN status */}
      {status === 'BROKEN' && (
        <div className="absolute inset-0 bg-red-500/10 animate-pulse" />
      )}
      
      {/* Header */}
      <div className={`text-[8px] text-${baseColor}/70 uppercase font-semibold flex items-center justify-center gap-1`}>
        <Lock className="w-2.5 h-2.5" />
        {label}
      </div>
      
      {/* Strike Price */}
      <div className={`text-sm font-bold text-${baseColor} font-mono relative z-10`} data-testid={`${type}-wall-strike`}>
        {strike?.toLocaleString() || '--'}
      </div>
      
      {/* Status Badge */}
      <div className="mt-0.5" data-testid={`${type}-wall-status`}>
        <WallStatusBadge 
          status={status} 
          oiStrength={oi_strength}
          isBreaking={is_breaking}
          breakDuration={break_duration}
        />
      </div>
      
      {/* GEX Value - SESSION 68f: 2 decimal places */}
      <div className="text-[8px] text-muted-foreground mt-0.5" data-testid={`${type}-wall-gex`}>
        {gex > 0 ? `+${gex?.toFixed(2)}Cr` : `${gex?.toFixed(2)}Cr`}
      </div>
      
      {/* Distance */}
      <div className={`text-[8px] ${status === 'BROKEN' ? 'text-red-400 font-bold' : 'text-muted-foreground'}`} data-testid={`${type}-wall-distance`}>
        {distanceDir}{Math.abs(distanceValue)}pts
      </div>
      
      {/* OI Stats (on hover tooltip) */}
      <div className="text-[6px] text-muted-foreground/60 mt-0.5" title={`Peak: ${peak_oi?.toLocaleString()} | Current: ${current_oi?.toLocaleString()}`} data-testid={`${type}-wall-oi`}>
        OI: {current_oi?.toLocaleString() || 0}
      </div>
    </div>
  );
});

WallCard.displayName = 'WallCard';

// Main KeyLevels Component
export const KeyLevels = memo(({ symbol = 'NIFTY', currentPrice = 0, showStats = false }) => {
  const [stickyLevels, setStickyLevels] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displaySymbol, setDisplaySymbol] = useState(symbol);  // Track which symbol's data we're showing
  
  // Fetch sticky levels
  const fetchStickyLevels = useCallback(async (targetSymbol) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const res = await fetch(`${API}/api/gex/sticky-levels?symbol=${targetSymbol}`, { headers });
      if (res.ok) {
        const data = await res.json();
        // Only update if we're still looking at the same symbol (avoid race conditions)
        setStickyLevels(prev => {
          // Verify the data matches the requested symbol
          if (data.symbol === targetSymbol) {
            setDisplaySymbol(targetSymbol);
            return data;
          }
          return prev;
        });
        setError(null);
      } else {
        setError('Failed to fetch levels');
      }
    } catch (err) {
      console.error('KeyLevels fetch error:', err);
      setError(err.message);
    }
    setIsLoading(false);
  }, []);
  
  // === CRITICAL: Clear state IMMEDIATELY when symbol changes (Session 40 Fix) ===
  // This prevents "ghost data" where old symbol's walls appear on new symbol
  useEffect(() => {
    // Reset state to show loading immediately - no ghost data!
    setStickyLevels(null);
    setIsLoading(true);
    setError(null);
    setDisplaySymbol(symbol);
    
    // Fetch fresh data for new symbol
    fetchStickyLevels(symbol);
    
    // Set up polling interval
    const interval = setInterval(() => fetchStickyLevels(symbol), 5000);
    return () => clearInterval(interval);
  }, [symbol, fetchStickyLevels]);
  
  // Show loading state during symbol switch
  if (isLoading || !stickyLevels || displaySymbol !== symbol) {
    return (
      <div className="p-2 text-center" data-testid="key-levels-loading">
        <div className="text-[10px] text-muted-foreground animate-pulse">
          Scanning {symbol} S&R levels...
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-2 text-center">
        <div className="text-[10px] text-red-400/70">S&R unavailable</div>
      </div>
    );
  }
  
  const { call_wall, put_wall, stats, spot_price } = stickyLevels;
  // SESSION 55 STRICT: Use currentPrice (futures) as reference for S&R distance calculations
  // spot_price is ONLY used as fallback when futures is unavailable
  const displayPrice = currentPrice || 0;
  
  return (
    <div className="space-y-1.5" data-testid="key-levels-container">
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-1">
          <Lock className="w-3 h-3 text-accent" />
          <span className="text-[9px] uppercase text-muted-foreground font-semibold">
            Sticky S&R
          </span>
        </div>
        <span className="text-[8px] text-muted-foreground">
          {symbol}
        </span>
      </div>
      
      {/* Walls Grid */}
      <div className="grid grid-cols-2 gap-1" data-testid="walls-grid">
        {/* Put Wall (Support) - Left */}
        <WallCard 
          type="put"
          wallData={put_wall}
          currentPrice={displayPrice}
        />
        
        {/* Call Wall (Resistance) - Right */}
        <WallCard 
          type="call"
          wallData={call_wall}
          currentPrice={displayPrice}
        />
      </div>
      
      {/* Session Stats (optional) */}
      {showStats && stats && (
        <div className="flex items-center justify-between text-[7px] text-muted-foreground px-1 pt-1 border-t border-border/20" data-testid="session-stats">
          <span data-testid="stat-changes">🔄 {stats.lock_changes_today || 0} changes</span>
          <span data-testid="stat-breaks">💥 {stats.price_breaks_today || 0} breaks</span>
          <span data-testid="stat-surrenders">📉 {stats.oi_surrenders_today || 0} surrenders</span>
        </div>
      )}
    </div>
  );
});

KeyLevels.displayName = 'KeyLevels';

export default KeyLevels;
